import { BaseCommand } from '../../commands/ICommand';
import type { FieldValue } from '../types';
import { useDiagramStore } from '../../../stores/diagramStore';
import type { CommandDeltaContribution } from '../../data-profiles/scenarios/ScenarioRecordable';
import { createNodeId } from '../../data-profiles/scenarios/ScenarioDelta';

export class UpdateNodeFieldCommand extends BaseCommand {
  private previousValue: FieldValue;
  private executedOnce = false;

  /**
   * Field edits participate in scenarios: while a scenario is being edited,
   * `ScenarioAwareCommand` records this contribution into the active scenario's
   * delta instead of writing the node. That is what lets a property-panel edit
   * become a scenario change (and later commit to the org model) rather than
   * mutating the chart directly.
   */
  readonly isScenarioRecordable = true as const;

  constructor(
    private nodeId: string,
    private fieldId: string,
    private value: FieldValue,
    private namespace?: string
  ) {
    super('Update Field', `Update field ${fieldId} on node ${nodeId}`);
    this.previousValue = undefined;
  }

  execute(): void {
    const store = useDiagramStore.getState();
    const node = store.nodes.find(n => n.id === this.nodeId);

    if (!node) {
      throw new Error(`Node ${this.nodeId} not found`);
    }

    if (!this.executedOnce) {
      if (this.namespace) {
        const namespaceObj = (node.attrs as any)?.[this.namespace] || {};
        this.previousValue = namespaceObj[this.fieldId];
      } else {
        this.previousValue = (node.attrs as any)?.[this.fieldId];
        if (this.previousValue === undefined) {
          this.previousValue = (node as any)[this.fieldId];
        }
      }
      this.executedOnce = true;
    }

    if (this.namespace) {
      const updatedNode = {
        ...node,
        attrs: {
          ...node.attrs,
          [this.namespace]: {
            ...(node.attrs as any)?.[this.namespace],
            [this.fieldId]: this.value,
          },
        },
      };
      store.updateNode(this.nodeId, updatedNode);
    } else {
      const updatedNode = {
        ...node,
        attrs: {
          ...node.attrs,
          [this.fieldId]: this.value,
        },
      };
      store.updateNode(this.nodeId, updatedNode);
    }
  }

  undo(): void {
    const store = useDiagramStore.getState();
    const node = store.nodes.find(n => n.id === this.nodeId);

    if (!node) {
      throw new Error(`Node ${this.nodeId} not found`);
    }

    if (this.namespace) {
      const updatedNode = {
        ...node,
        attrs: {
          ...node.attrs,
          [this.namespace]: {
            ...(node.attrs as any)?.[this.namespace],
            [this.fieldId]: this.previousValue,
          },
        },
      };
      store.updateNode(this.nodeId, updatedNode);
    } else {
      const updatedNode = {
        ...node,
        attrs: {
          ...node.attrs,
          [this.fieldId]: this.previousValue,
        },
      };
      store.updateNode(this.nodeId, updatedNode);
    }
  }

  /** Field id as recorded in a scenario delta (namespaced fields stay qualified). */
  private deltaField(): string {
    return this.namespace ? `${this.namespace}.${this.fieldId}` : this.fieldId;
  }

  private currentValue(): FieldValue {
    const node = useDiagramStore.getState().nodes.find((n) => n.id === this.nodeId);
    const attrs = (node?.attrs ?? {}) as Record<string, unknown>;
    if (this.namespace) {
      const namespaceObj = (attrs[this.namespace] ?? {}) as Record<string, unknown>;
      return namespaceObj[this.fieldId] as FieldValue;
    }
    const value = attrs[this.fieldId];
    return (value === undefined ? (node as Record<string, unknown> | undefined)?.[this.fieldId] : value) as FieldValue;
  }

  toScenarioDelta(): CommandDeltaContribution {
    return {
      updates: [{
        nodeId: createNodeId(this.nodeId),
        change: { field: this.deltaField(), oldValue: this.currentValue(), newValue: this.value },
      }],
    };
  }

  getUndoDelta(): CommandDeltaContribution {
    return this.toScenarioDelta();
  }
}
